/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { IFormElementInlineConfig } from "./IFormElementInlineConfig";

/**
 * Element config
 */
export interface IFormFieldBaseConfig<TValue> extends IFormElementConfig, IFormElementInlineConfig {
	id: string;
	label?: string;
	required?: boolean;
	readOnly?: boolean;
	value?: TValue;
	labelAlign?: "left"|"center"|"right";
	verticalAlign?: "top"|"center"|"bottom";
	style?: "default"|"summary";
}

/**
 * Field Base class
 */
export abstract class FormFieldBase<TConfig extends IFormFieldBaseConfig<any>> extends FormElement<TConfig> {

	/** Value ID */
	protected valueId: string;

	/** Current field value */
	protected value: TConfig["value"];

	/** If the field is valid */
	protected valid: boolean;

	/** If the field value was modified */
	protected pristine: boolean = false;

	/** If the field was touched by user */
	protected touched: boolean = false;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		model: FormModel,
		parent: FormElement<any>,
		type: FORM_EL_TYPE,
		config: TConfig,
		protected initialValue: TConfig["value"]
	) {

		super(model, parent, type, config);

		this.valueId = this.path
			.filter((item) => item.id ? true : false)
			.map((item) => item.id)
			.join("_");

		this.value = this.model.get(this.valueId) || this.config.value || initialValue;

		model.set(this.valueId, this.value, false);

		this.validate();

	}

	/**
	 * Validates the value - should be overriden
	 */
	protected validate() {

		this.valid = true;

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		const newValue = this.model.get(this.valueId);

		// console.log("UPDATE GET", this.valueId, newValue);

		if (resetState) {

			this.pristine = true;
			this.touched = false;
			this.value = newValue || this.config.value || this.initialValue;

			this.validate();
			this.emit("update");

		} else if (newValue !== this.value) {

			this.value = newValue;
			this.validate();
			this.emit("update");

		}

	}

	/**
	 * Sets the field as touched
	 */
	public touch() {

		this.touched = true;
		this.emit("update");

	}

	/**
	 * Sets the field value
	 *
	 * @param value New value
	 * @param update If to update the model
	 */
	public set(value: TConfig["value"], update: boolean = true) {

		// console.log("SET", this.valueId, value);

		this.touched = true;
		this.model.set(this.valueId, value, update);

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			valueId: this.valueId,
			value: this.value,
			valid: this.valid,
			pristine: this.pristine,
			touched: this.touched
		};

	}

	/**
	 * Destroys the element
	 */
	public destroy() {

		this.model.unset(this.valueId);
		super.destroy();

	}

}
